/**
 * PDP June 2026 – mix & match bundle with Skio Dynamic Box cart integration.
 * @see https://help.skio.com/docs/dynamic-box-api-guide-for-developers
 */

const SKIO_DYNAMIC_BOX_API = 'https://api.skio.com/storefront-http/get-dynamic-box';

const PDP_JUNE_CONFIG = {
  freeShippingThreshold: 35.99,
  sticksPerPouch: 12,
  subscribeDiscount: 0.2,
  subscribeFirstOrderExtraDiscount: 0.15,
  otpDiscounts: { 3: 10, 4: 15 },
  otpTierPerks: ['', '', '$10 OFF', '$15 OFF'],
  frequencyByPouches: { 1: 1, 2: 1, 3: 2, 4: 3 },
  tiers: [
    { min: 1, label: '1 Pouch', perks: ['20% OFF'] },
    { min: 2, label: '2 Pouches', perks: ['20% OFF', 'FREE SHIPPING'] },
    { min: 3, label: '3 Pouches', perks: ['20% OFF', 'FREE SHIPPING', 'FREE GIFT'] },
    { min: 4, label: '4+ Pouches', perks: ['35% OFF', 'FREE SHIPPING', 'FREE GIFT'] },
  ],
};

class PdpJune2026 extends HTMLElement {
  constructor() {
    super();
    this.flavors = [];
    this.quantities = {};
    this.purchaseType = 'subscribe';
    this.frequencyMonths = 1;
    this.stickPriceCents = 0;
    this.currency = '';
    this.sectionId = '';
    this.productUrl = '';
    this.imagesFromMetafields = true;
    this.galleryAbortController = null;
    this.activeGalleryVariantId = null;
    this.skioDynamicBoxProductId = '';
    this.skioSellingPlans = {};
    this.skioBoxId = null;
    this.skioBoxLoadPromise = null;
    this.skioEligibleVariantIds = null;
    this.autoFillUsed = false;
    this.autoFillLabel = 'AUTO FILL BESTSELLERS';
  }

  connectedCallback() {
    this.parseConfig();
    this.cacheElements();
    this.bindEvents();
    this.renderFlavorList();
    this.loadSkioDynamicBox();
    this.setupBuilderSticky();
    this.updateUI();
  }

  parseConfig() {
    const dataEl = this.querySelector('[data-pdp-june-config]');
    if (!dataEl) return;
    const config = JSON.parse(dataEl.textContent);
    this.flavors = config.flavors || [];
    this.bestsellerIds = (config.bestsellerVariantIds || []).map(String);
    this.defaultPackSize = config.defaultPackSize || '12pk';
    this.sticksPerPouch = config.sticksPerPouch || PDP_JUNE_CONFIG.sticksPerPouch;
    this.stickPriceCents = this.resolveStickPriceCents(config.stickPriceCents || 0);
    this.currency = config.currency || '$';
    this.applyFreeGiftConfig(config);
    this.sectionId = config.sectionId || '';
    this.productUrl = config.productUrl || '';
    this.imagesFromMetafields = config.imagesFromMetafields !== false;

    const skio = config.skio || {};
    this.skioDynamicBoxProductId = String(skio.dynamicBoxProductId || '').trim();
    this.skioSellingPlans = skio.sellingPlans || {};

    this.flavors.forEach((f) => {
      this.quantities[f.variantId] = 0;
    });
  }

  resolveStickPriceCents(fallbackCents = 0) {
    const standardPack = this.flavors.find((flavor) => {
      const sticks = Number(flavor.sticksPerUnit);
      return Number.isFinite(sticks) && sticks === this.sticksPerPouch && flavor.priceCents > 0;
    });

    if (standardPack) {
      return Math.floor(standardPack.priceCents / this.sticksPerPouch);
    }

    return fallbackCents;
  }

  cacheElements() {
    this.tierFill = this.querySelector('[data-tier-fill]');
    this.tierMarkers = this.querySelectorAll('[data-tier-marker]');
    this.tierMarkers?.forEach((marker) => {
      const perkEl = marker.querySelector('.pdp-june-tier__node-perk');
      if (perkEl) perkEl.dataset.subscribePerk = perkEl.textContent.trim();
    });
    this.flavorList = this.querySelector('[data-flavor-list]');
    this.flavorPanel = this.querySelector('[data-flavor-panel]');
    this.autoFillBtn = this.querySelector('[data-auto-fill]');
    if (this.autoFillBtn) {
      this.autoFillLabel =
        this.autoFillBtn.dataset.autoFillLabel?.trim() || this.autoFillBtn.textContent.trim() || this.autoFillLabel;
    }
    this.purchaseSection = this.querySelector('[data-purchase-section]');
    this.purchaseOptions = this.querySelectorAll('[name="pdp-june-purchase-type"]');
    this.frequencySelect = this.querySelector('[data-frequency-select]');
    this.subscribeDetails = this.querySelector('[data-subscribe-details]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.querySelector('.product-form__error-message');
    this.cart = this.getCartComponent();
  }

  getCartComponent() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  getCartSectionIds(cart = this.getCartComponent()) {
    if (cart?.tagName === 'CART-DRAWER') {
      return ['cart-drawer', 'cart-icon-bubble'];
    }
    if (typeof cart?.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((section) => section.id);
    }
    return ['cart-icon-bubble'];
  }

  openCartUi(cart = this.getCartComponent()) {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartRefreshCart?.();
      window.upcartOpenCart();
      return true;
    }

    if (!cart) return false;
    cart.setActiveElement?.(this.addToCartBtn);
    if (typeof cart.open === 'function') {
      cart.open(this.addToCartBtn);
      return true;
    }
    return false;
  }

  async refreshCartIconBubble() {
    const sectionsUrl = `${window.location.pathname}?sections=cart-icon-bubble`;
    const sectionsResponse = await fetch(sectionsUrl);
    if (!sectionsResponse.ok) return;

    const sectionsData = await sectionsResponse.json();
    const bubbleHtml = sectionsData['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !bubbleHtml) return;

    const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
    if (parsed) bubble.innerHTML = parsed.innerHTML;
  }

  bindEvents() {
    this.autoFillBtn?.addEventListener('click', () => this.handleAutoFillClick());
    this.purchaseOptions.forEach((radio) => {
      radio.addEventListener('change', () => {
        this.purchaseType = radio.value;
        this.updateUI();
      });
    });
    this.frequencySelect?.addEventListener('change', () => {
      this.frequencyMonths = parseInt(this.frequencySelect.value, 10);
    });
    this.addToCartBtn?.addEventListener('click', (e) => this.handleAddToCart(e));
    this._onTierResize = () => this.updateTierFill();
    window.addEventListener('resize', this._onTierResize);
  }

  disconnectedCallback() {
    if (this._onTierResize) {
      window.removeEventListener('resize', this._onTierResize);
    }
    this._builderStickyObserver?.disconnect();
  }

  setupBuilderSticky() {
    const sticky = this.querySelector('[data-builder-sticky]');
    const sentinel = this.querySelector('[data-builder-sticky-sentinel]');
    if (!sticky || !sentinel || !('IntersectionObserver' in window)) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const syncPinnedState = (entries) => {
      if (!mobileQuery.matches) {
        sticky.classList.remove('is-pinned');
        return;
      }
      const entry = entries?.[0];
      if (!entry) return;
      sticky.classList.toggle('is-pinned', !entry.isIntersecting);
      this.updateTierFill();
    };

    this._builderStickyObserver?.disconnect();
    this._builderStickyObserver = new IntersectionObserver(syncPinnedState, {
      threshold: 0,
    });
    this._builderStickyObserver.observe(sentinel);

    const onBreakpointChange = () => {
      if (!mobileQuery.matches) {
        sticky.classList.remove('is-pinned');
      }
      this.updateTierFill();
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else {
      mobileQuery.addListener(onBreakpointChange);
    }
  }

  isSkioEnabled() {
    return Boolean(this.skioDynamicBoxProductId);
  }

  applyFreeGiftConfig(config = {}) {
    let freeGiftVariantId = config.freeGiftVariantId;
    let freeGiftPriceCents = config.freeGiftPriceCents;

    const guardConfigEl = document.getElementById('pdp-june-cart-guard-config');
    if (guardConfigEl) {
      try {
        const guardConfig = JSON.parse(guardConfigEl.textContent);
        freeGiftVariantId = freeGiftVariantId || guardConfig.freeGiftVariantId;
        if (freeGiftPriceCents == null) {
          freeGiftPriceCents = guardConfig.freeGiftPriceCents;
        }
      } catch (error) {
        console.error(error);
      }
    }

    this.freeGiftVariantId = this.parseShopifyNumericId(freeGiftVariantId);
    this.freeGiftPriceCents = freeGiftPriceCents || 0;
  }

  parseShopifyNumericId(value) {
    if (value == null) return null;
    const stringValue = String(value);
    const gidMatch = stringValue.match(/\/(\d+)$/);
    if (gidMatch) return gidMatch[1];
    return stringValue.replace(/\D/g, '') || null;
  }

  loadSkioDynamicBox() {
    if (!this.isSkioEnabled()) return Promise.resolve(null);

    if (this.skioBoxLoadPromise) return this.skioBoxLoadPromise;

    this.skioBoxLoadPromise = fetch(SKIO_DYNAMIC_BOX_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productPlatformId: this.skioDynamicBoxProductId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data?.ok || !data?.DynamicBox?.boxId) {
          throw new Error('Skio dynamic box data is unavailable.');
        }

        this.skioBoxId = data.DynamicBox.boxId;
        this.skioEligibleVariantIds = new Set();

        (data.DynamicBox.selectableProductVariants || []).forEach((product) => {
          (product.productVariants || []).forEach((variant) => {
            const numericId = this.parseShopifyNumericId(variant.platformId);
            if (numericId) this.skioEligibleVariantIds.add(numericId);
          });
        });

        return data.DynamicBox;
      })
      .catch((error) => {
        console.error(error);
        this.skioBoxId = null;
        return null;
      });

    return this.skioBoxLoadPromise;
  }

  async ensureSkioReady() {
    if (!this.isSkioEnabled()) return true;
    await this.loadSkioDynamicBox();
    return Boolean(this.skioBoxId);
  }

  getSellingPlanId() {
    const planId = this.skioSellingPlans[String(this.frequencyMonths)];
    if (!planId) return null;
    return parseInt(String(planId).trim(), 10);
  }

  getDynamicBoxIndex() {
    return 0;
  }

  buildSkioBoxProperties(extra = {}) {
    if (!this.skioBoxId) return { ...extra };

    return {
      ...extra,
      _dynamicBoxId: this.skioBoxId,
      _dynamicBoxIndex: this.getDynamicBoxIndex(),
    };
  }

  buildLineItemProperties(extra = {}) {
    const isSubscribe = this.purchaseType === 'subscribe';
    if (isSubscribe && this.isSkioEnabled()) {
      return this.buildSkioBoxProperties(extra);
    }
    return { ...extra };
  }

  async fetchCart() {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  cartContainsFreeGift(cart) {
    if (!cart?.items?.length || !this.freeGiftVariantId) return false;

    const giftVariantId = String(this.freeGiftVariantId);
    return cart.items.some((item) => String(item.variant_id) === giftVariantId);
  }

  qualifiesForFreeGift(cart) {
    if (this.purchaseType !== 'subscribe') return false;
    if (this.getTierUnits() < 3) return false;
    if (!this.freeGiftVariantId) return false;
    if (this.cartContainsFreeGift(cart)) return false;
    return true;
  }

  getFreeGiftConfigError() {
    return null;
  }

buildFreeGiftLineItem() {
    return {
      id: parseInt(String(this.freeGiftVariantId), 10),
      quantity: 1,
      properties: { _free_gift: 'true', _pdp_june_2026: 'true' },
    };
  }

  getPouchCount() {
    return Object.values(this.quantities).reduce((sum, q) => sum + q, 0);
  }

  getFlavorTierUnits(flavor) {
    const units = Number(flavor?.tierUnits);
    return Number.isFinite(units) && units > 0 ? units : 1;
  }

  getTierUnits() {
    return this.flavors.reduce((sum, flavor) => {
      const qty = this.quantities[flavor.variantId] || 0;
      return sum + qty * this.getFlavorTierUnits(flavor);
    }, 0);
  }

  getActiveTierIndex() {
    const count = this.getTierUnits();
    if (count >= 4) return 3;
    if (count >= 3) return 2;
    if (count >= 2) return 1;
    if (count >= 1) return 0;
    return -1;
  }

  getDefaultFrequency() {
    const count = this.getTierUnits();
    if (count >= 4) return 3;
    if (count >= 3) return 2;
    return 1;
  }

  getSubscribeDiscountRate() {
    const count = this.getTierUnits();
    if (count >= 4) {
      return PDP_JUNE_CONFIG.subscribeDiscount + PDP_JUNE_CONFIG.subscribeFirstOrderExtraDiscount;
    }
    return PDP_JUNE_CONFIG.subscribeDiscount;
  }

  getOtpDiscountDollars() {
    const count = this.getTierUnits();
    if (count >= 4) return PDP_JUNE_CONFIG.otpDiscounts[4];
    if (count >= 3) return PDP_JUNE_CONFIG.otpDiscounts[3];
    return 0;
  }

  formatMoney(cents) {
    return `${this.currency}${(cents / 100).toFixed(2)}`;
  }

  // Matches Shopify checkout: discount amount is floored, then subtracted.
  applyShopifyPercentDiscount(priceCents, rate) {
    if (!priceCents || !rate) return priceCents || 0;
    return priceCents - Math.floor(priceCents * rate);
  }

  getPricePerStickCents() {
    if (!this.stickPriceCents) return 0;
    if (this.purchaseType === 'subscribe') {
      return this.applyShopifyPercentDiscount(this.stickPriceCents, this.getSubscribeDiscountRate());
    }
    return this.stickPriceCents;
  }

  getOriginalSubtotalCents() {
    let total = 0;
    this.flavors.forEach((f) => {
      const qty = this.quantities[f.variantId] || 0;
      total += f.priceCents * qty;
    });
    return total;
  }

  getSubscribeSubtotalCents() {
    const rate = this.getSubscribeDiscountRate();
    let total = 0;
    this.flavors.forEach((f) => {
      const qty = this.quantities[f.variantId] || 0;
      if (qty <= 0) return;
      total += this.applyShopifyPercentDiscount(f.priceCents, rate) * qty;
    });
    return total;
  }

  getOtpSubtotalCents() {
    const original = this.getOriginalSubtotalCents();
    if (!original) return 0;
    return Math.max(0, original - this.getOtpDiscountDollars() * 100);
  }

  updateTotalPricing({ wrapper, compareEl, finalEl, compareCents, finalCents }) {
    if (!wrapper || !compareEl || !finalEl) return;

    if (!finalCents) {
      wrapper.hidden = true;
      compareEl.textContent = '';
      finalEl.textContent = '';
      return;
    }

    wrapper.hidden = false;
    finalEl.textContent = this.formatMoney(finalCents);
    compareEl.textContent = compareCents > finalCents ? this.formatMoney(compareCents) : '';
  }

  getSubtotalCents() {
    return this.purchaseType === 'subscribe'
      ? this.getSubscribeSubtotalCents()
      : this.getOtpSubtotalCents();
  }

  renderFlavorList() {
    if (!this.flavorList) return;
    if (!this.flavors.length) {
      this.flavorList.innerHTML = `
        <p class="pdp-june-flavors__empty">
          No flavors configured. Add Flavor blocks in the theme editor, or set a <strong>Flavor source product</strong> (e.g. weekend-recovery).
        </p>
      `;
      return;
    }
    this.flavorList.innerHTML = this.flavors
      .map(
        (f) => `
      <div class="pdp-june-flavor${f.available === false ? ' pdp-june-flavor--sold-out' : ''}" data-flavor-id="${f.variantId}">
        <div class="pdp-june-flavor__image">
          ${f.image ? `<img src="${f.image}" alt="${f.title}" width="56" height="56" loading="lazy">` : ''}
        </div>
        <div class="pdp-june-flavor__info">
          <p class="pdp-june-flavor__title">${f.title}</p>
          <p class="pdp-june-flavor__size">${f.sizeLabel || this.defaultPackSize}</p>
        </div>
        <div class="pdp-june-flavor__actions">
          <button type="button" class="pdp-june-flavor__add" data-add-flavor="${f.variantId}" ${f.available === false ? 'disabled' : ''}>
            Add +
          </button>
          <div class="pdp-june-flavor__qty hidden" data-qty-control="${f.variantId}">
            <button type="button" class="pdp-june-flavor__qty-btn" data-qty-minus="${f.variantId}" aria-label="Decrease quantity">−</button>
            <span class="pdp-june-flavor__qty-value" data-qty-value="${f.variantId}">0</span>
            <button type="button" class="pdp-june-flavor__qty-btn" data-qty-plus="${f.variantId}" aria-label="Increase quantity">+</button>
          </div>
        </div>
      </div>
    `
      )
      .join('');

    this.flavorList.querySelectorAll('[data-add-flavor]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setFlavorQty(btn.dataset.addFlavor, 1);
      });
    });
    this.flavorList.querySelectorAll('[data-qty-minus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.qtyMinus;
        this.setFlavorQty(id, (this.quantities[id] || 0) - 1);
      });
    });
    this.flavorList.querySelectorAll('[data-qty-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.qtyPlus;
        this.setFlavorQty(id, (this.quantities[id] || 0) + 1);
      });
    });
  }

  setFlavorQty(variantId, qty) {
    this.quantities[variantId] = Math.max(0, qty);
    this.syncFlavorControls(variantId);
    if (this.purchaseType === 'subscribe' && this.frequencySelect) {
      const freq = this.getDefaultFrequency();
      this.frequencyMonths = freq;
      this.frequencySelect.value = String(freq);
    }
    this.updateUI();
  }

  syncFlavorControls(variantId) {
    const qty = this.quantities[variantId] || 0;
    const addBtn = this.querySelector(`[data-add-flavor="${variantId}"]`);
    const qtyControl = this.querySelector(`[data-qty-control="${variantId}"]`);
    const qtyValue = this.querySelector(`[data-qty-value="${variantId}"]`);
    if (!addBtn || !qtyControl) return;
    if (qty === 0) {
      addBtn.classList.remove('hidden');
      qtyControl.classList.add('hidden');
    } else {
      addBtn.classList.add('hidden');
      qtyControl.classList.remove('hidden');
      if (qtyValue) qtyValue.textContent = qty;
    }
  }

  autoFillBestsellers() {
    Object.keys(this.quantities).forEach((id) => {
      this.quantities[id] = 0;
    });
    const ids =
      this.bestsellerIds.length >= 3
        ? this.bestsellerIds.slice(0, 3)
        : this.flavors.slice(0, 3).map((f) => String(f.variantId));
    ids.forEach((id) => {
      this.quantities[id] = 1;
      this.syncFlavorControls(id);
    });
    if (this.frequencySelect) {
      this.frequencyMonths = 2;
      this.frequencySelect.value = '2';
    }
    this.updateUI();
  }

  handleAutoFillClick() {
    if (this.autoFillUsed) {
      this.scrollToPurchaseSection();
      return;
    }

    this.autoFillUsed = true;
    this.autoFillBestsellers();
  }

  updateAutoFillButton() {
    if (!this.autoFillBtn) return;

    if (this.getPouchCount() === 0) {
      this.autoFillUsed = false;
      this.autoFillBtn.textContent = this.autoFillLabel;
      this.autoFillBtn.classList.remove('is-checkout');
      return;
    }

    if (this.autoFillUsed) {
      this.autoFillBtn.textContent = 'CHECKOUT';
      this.autoFillBtn.classList.add('is-checkout');
      return;
    }

    this.autoFillBtn.textContent = this.autoFillLabel;
    this.autoFillBtn.classList.remove('is-checkout');
  }

  scrollToPurchaseSection() {
    const target = this.purchaseSection || this.querySelector('.pdp-june-purchase');
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateTierBar() {
    const tierIndex = this.getActiveTierIndex();
    const isOtp = this.purchaseType === 'onetime';
    const tierMarkers = this.querySelectorAll('[data-tier-marker]');
    tierMarkers.forEach((marker, i) => {
      marker.classList.toggle('is-active', i <= tierIndex);
      marker.classList.toggle('is-current', i === tierIndex);

      const perkEl = marker.querySelector('.pdp-june-tier__node-perk');
      if (!perkEl) return;

      const perkText = isOtp
        ? (PDP_JUNE_CONFIG.otpTierPerks[i] || '')
        : (perkEl.dataset.subscribePerk || '');
      perkEl.textContent = perkText;
      perkEl.classList.toggle('is-empty', !perkText);
    });

    this.updateTierFill(tierIndex);
  }

  updateTierFill(tierIndex = this.getActiveTierIndex()) {
    const track = this.querySelector('[data-tier-track]') || this.querySelector('.pdp-june-tier__track');
    const tierMarkers = this.querySelectorAll('[data-tier-marker]');

    if (!track) return;

    if (tierIndex < 0) {
      track.style.setProperty('--tier-fill', '0%');
      return;
    }

    const applyFill = () => {
      const trackRect = track.getBoundingClientRect();
      if (!trackRect.width) return;

      const marker = tierMarkers[tierIndex];
      if (!marker) return;

      const dot = marker.querySelector('.pdp-june-tier__node-dot');
      const dotHidden = dot && getComputedStyle(dot).visibility === 'hidden';
      const target = dot && !dotHidden ? dot : marker;
      const targetRect = target.getBoundingClientRect();
      const targetCenter = targetRect.left + targetRect.width / 2 - trackRect.left;
      const isLastTier = tierIndex >= tierMarkers.length - 1;
      const percent = isLastTier
        ? 100
        : Math.max(0, Math.min(100, (targetCenter / trackRect.width) * 100));

      track.style.setProperty('--tier-fill', `${percent}%`);
    };

    applyFill();
    requestAnimationFrame(applyFill);
  }

  updatePurchaseCopy() {
    const sub20 = this.querySelector('[data-sub-price-20]');
    const sub35 = this.querySelector('[data-sub-price-35]');
    if (sub20 && this.stickPriceCents) {
      const price20 = this.applyShopifyPercentDiscount(
        this.stickPriceCents,
        PDP_JUNE_CONFIG.subscribeDiscount
      );
      sub20.textContent = this.formatMoney(price20);
    }
    if (sub35 && this.stickPriceCents) {
      const rate35 = PDP_JUNE_CONFIG.subscribeDiscount + PDP_JUNE_CONFIG.subscribeFirstOrderExtraDiscount;
      const price35 = this.applyShopifyPercentDiscount(this.stickPriceCents, rate35);
      sub35.textContent = this.formatMoney(price35);
    }

    const originalSubtotal = this.getOriginalSubtotalCents();
    this.updateTotalPricing({
      wrapper: this.querySelector('[data-sub-total-pricing]'),
      compareEl: this.querySelector('[data-sub-compare-price]'),
      finalEl: this.querySelector('[data-sub-final-price]'),
      compareCents: originalSubtotal,
      finalCents: this.getSubscribeSubtotalCents(),
    });
    this.updateTotalPricing({
      wrapper: this.querySelector('[data-otp-total-pricing]'),
      compareEl: this.querySelector('[data-otp-compare-price]'),
      finalEl: this.querySelector('[data-otp-final-price]'),
      compareCents: originalSubtotal,
      finalCents: this.getOtpSubtotalCents(),
    });

    const otpPrice = this.querySelector('[data-otp-stick-price]');
    if (otpPrice) otpPrice.textContent = this.stickPriceCents ? this.formatMoney(this.stickPriceCents) : '$2.08';

    const otpExtras = this.querySelector('[data-otp-extras]');
    if (otpExtras) {
      const discount = this.getOtpDiscountDollars();
      otpExtras.innerHTML =
        discount > 0
          ? `<span class="pdp-june-otp-discount">$${discount} off</span>`
          : '';
    }

    const subCard = this.querySelector('[data-purchase-card="subscribe"]');
    const otpCard = this.querySelector('[data-purchase-card="onetime"]');
    subCard?.classList.toggle('is-selected', this.purchaseType === 'subscribe');
    otpCard?.classList.toggle('is-selected', this.purchaseType === 'onetime');

    const purchaseBox = this.querySelector('.pdp-june-purchase');
    if (purchaseBox) {
      purchaseBox.classList.toggle('has-subscribe-selected', this.purchaseType === 'subscribe');
    }

    if (this.subscribeDetails) this.subscribeDetails.hidden = this.purchaseType !== 'subscribe';

    if (this.frequencySelect && this.purchaseType === 'subscribe') {
      const freq = this.getDefaultFrequency();
      if (document.activeElement !== this.frequencySelect) {
        this.frequencyMonths = freq;
        this.frequencySelect.value = String(freq);
      }
    }
  }

  updateCheckoutButtons() {
    const count = this.getPouchCount();
    if (!this.addToCartBtn) return;

    const hasFlavors = count > 0;
    this.addToCartBtn.disabled = false;
    this.addToCartBtn.setAttribute('aria-disabled', 'false');
    this.addToCartBtn.textContent = hasFlavors ? 'Add to Cart' : 'SELECT FLAVORS';
    this.addToCartBtn.classList.toggle('is-select-flavors', !hasFlavors);
  }

  scrollToFlavorSelection() {
    const target = this.flavorPanel || this.flavorList;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateUI() {
    this.flavors.forEach((f) => this.syncFlavorControls(String(f.variantId)));
    this.updateTierBar();
    this.updatePurchaseCopy();
    this.updateAutoFillButton();
    this.updateCheckoutButtons();
  }

  buildFlavorCartItems() {
    const items = [];
    const tierUnits = this.getTierUnits();
    const isSubscribe = this.purchaseType === 'subscribe';
    const sellingPlanId = isSubscribe ? this.getSellingPlanId() : null;

    const trackingProps = {
      _pdp_june_2026: 'true',
      _purchase_type: this.purchaseType,
      _pouch_count: String(tierUnits),
      _tier_units_total: String(tierUnits),
      _delivery_frequency_months: String(this.frequencyMonths),
      _subscribe_discount: isSubscribe ? String(Math.round(this.getSubscribeDiscountRate() * 100)) : '0',
    };

    this.flavors.forEach((f) => {
      const qty = this.quantities[f.variantId] || 0;
      if (qty <= 0) return;

      const lineTierUnits = qty * this.getFlavorTierUnits(f);
      const tierUnitsPerItem = this.getFlavorTierUnits(f);
      const variantId = parseInt(String(f.variantId), 10);
      const lineItem = {
        id: variantId,
        quantity: qty,
        properties: this.buildLineItemProperties({
          ...trackingProps,
          _flavor: f.title,
          _tier_units: String(lineTierUnits),
          _tier_units_per_item: String(tierUnitsPerItem),
          _sticks_per_unit: String(f.sticksPerUnit || this.sticksPerPouch),
          _sticks_per_pouch: String(this.sticksPerPouch),
        }),
      };

      if (isSubscribe && sellingPlanId) {
        lineItem.selling_plan = sellingPlanId;
      }

      items.push(lineItem);
    });

    return items;
  }

  buildCartItems(cart = null) {
    const items = this.buildFlavorCartItems();
    if (this.qualifiesForFreeGift(cart)) {
      items.push(this.buildFreeGiftLineItem());
    }
    return items;
  }

  async addFreeGiftToCart(cartComponent) {
    if (!this.buildFreeGiftLineItem().id) return false;

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [this.buildFreeGiftLineItem()] }),
    });

    try {
      const data = await this.parseCartAddResponse(response);
      if (data?.status) {
        console.warn('Free gift could not be added:', data.description || data.message);
        return false;
      }
      if (cartComponent || typeof window.upcartRefreshCart === 'function') {
        await this.refreshCartSections();
      }
      return true;
    } catch (error) {
      console.warn('Free gift could not be added:', error);
      return false;
    }
  }

  validateCartItems(items) {
    if (!items.length) {
      return 'Please add at least one flavor.';
    }

    const freeGiftError = this.getFreeGiftConfigError();
    if (freeGiftError) {
      return freeGiftError;
    }

    if (this.purchaseType !== 'subscribe') {
      return null;
    }

    if (this.isSkioEnabled() && !this.skioBoxId) {
      return 'Subscription box is still loading. Please try again.';
    }

    if (!this.getSellingPlanId()) {
      return 'Subscription plan is not configured. Check theme settings.';
    }

    if (this.skioEligibleVariantIds) {
      const invalid = items.find((item) => !this.skioEligibleVariantIds.has(String(item.id)));
      if (invalid) {
        return 'One or more selected flavors are not available in this subscription box.';
      }
    }

    return null;
  }

  getMediaGallery() {
    if (!this.sectionId) return null;
    return document.getElementById(`MediaGallery-${this.sectionId}`);
  }

  updateSectionVariantStyles(html) {
    if (!this.sectionId) return;

    const sourceSection = html.getElementById(`MainProduct-${this.sectionId}`);
    const destinationSection = document.getElementById(`MainProduct-${this.sectionId}`);
    if (!sourceSection || !destinationSection) return;

    const sourceStyle = sourceSection.querySelector('style');
    const destinationStyle = destinationSection.querySelector('style');
    if (sourceStyle && destinationStyle) {
      destinationStyle.textContent = sourceStyle.textContent;
    }
  }

  getFeaturedMediaId(html) {
    const mediaGallery = html.getElementById(`MediaGallery-${this.sectionId}`);
    if (!mediaGallery) return null;

    const activeSlide = mediaGallery.querySelector('[data-media-id].is-active');
    if (activeSlide?.dataset.mediaId) return activeSlide.dataset.mediaId;

    const firstSlide = mediaGallery.querySelector('[data-media-id]');
    return firstSlide?.dataset.mediaId || null;
  }

  syncMediaGallerySlides(sourceGallery, destinationGallery) {
    const sourceItems = Array.from(sourceGallery.querySelectorAll('li[data-media-id]'));
    const destinationList = destinationGallery.querySelector('ul');
    if (!destinationList || !sourceItems.length) return;

    const sourceSet = new Set(sourceItems.map((item) => item.dataset.mediaId));
    const destinationItems = Array.from(destinationList.querySelectorAll('li[data-media-id]'));

    destinationItems.forEach((item) => {
      if (!sourceSet.has(item.dataset.mediaId)) item.remove();
    });

    sourceItems.forEach((sourceItem) => {
      const existingItem = destinationList.querySelector(`[data-media-id="${sourceItem.dataset.mediaId}"]`);
      if (!existingItem) {
        destinationList.appendChild(sourceItem.cloneNode(true));
      }
    });

    const orderedItems = Array.from(sourceItems).map((sourceItem) =>
      destinationList.querySelector(`[data-media-id="${sourceItem.dataset.mediaId}"]`)
    );
    orderedItems.forEach((item) => {
      if (item) destinationList.appendChild(item);
    });
  }

  async updateGalleryForVariant(variantId) {
    if (!this.sectionId || !this.productUrl || !variantId) return;
    if (String(this.activeGalleryVariantId) === String(variantId)) return;

    this.galleryAbortController?.abort();
    this.galleryAbortController = new AbortController();

    const requestUrl = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(requestUrl, { signal: this.galleryAbortController.signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const mediaGallery = this.getMediaGallery();
      const sourceGallery = html.getElementById(`MediaGallery-${this.sectionId}`);

      if (!mediaGallery || !sourceGallery) return;

      this.updateSectionVariantStyles(html);

      if (this.imagesFromMetafields) {
        mediaGallery.innerHTML = sourceGallery.innerHTML;
        mediaGallery.init?.();
      } else {
        this.syncMediaGallerySlides(sourceGallery, mediaGallery);
        const featuredMediaId = this.getFeaturedMediaId(html);
        if (featuredMediaId) {
          mediaGallery.setActiveMedia?.(featuredMediaId, true);
        }
      }

      this.activeGalleryVariantId = String(variantId);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  showError(message) {
    if (!this.errorWrapper || !this.errorMessage) return;
    this.errorWrapper.hidden = !message;
    if (message) this.errorMessage.textContent = message;
  }

  async refreshCartSections() {
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
      this.openCartUi();
      return;
    }

    const cart = this.getCartComponent();
    if (!cart) {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    if (cart.tagName !== 'CART-DRAWER') {
      await this.refreshCartIconBubble();
      this.openCartUi(cart);
      return;
    }

    const sectionIds = this.getCartSectionIds(cart);
    const sectionsUrl = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
    const sectionsResponse = await fetch(sectionsUrl);

    if (!sectionsResponse.ok) {
      throw new Error('Could not refresh cart sections.');
    }

    const sectionsData = await sectionsResponse.json();
    cart.setActiveElement?.(this.addToCartBtn);

    try {
      cart.renderContents({ sections: sectionsData });
    } catch (renderError) {
      console.error(renderError);
      await this.refreshCartIconBubble();
      this.openCartUi(cart);
    }
  }

  async parseCartAddResponse(response) {
    const responseText = await response.text();
    let data = null;

    try {
      data = responseText ? JSON.parse(responseText) : null;
    } catch (parseError) {
      console.error(parseError, responseText);
      throw new Error('Unexpected response from cart.');
    }

    if (!response.ok) {
      throw new Error(data?.description || data?.message || 'Could not add to cart.');
    }

    return data;
  }

  async renderCartAfterAdd(data) {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartRefreshCart?.();
      window.upcartOpenCart();
      return;
    }

    const cart = this.getCartComponent();
    if (!cart) {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    cart.setActiveElement?.(this.addToCartBtn);

    const hasSections = data?.sections && Object.keys(data.sections).length > 0;
    if (hasSections && typeof cart.renderContents === 'function') {
      try {
        cart.renderContents(data);
        return;
      } catch (renderError) {
        console.error(renderError);
      }
    }

    await this.refreshCartSections();
  }

  async handleAddToCart(e) {
    if (e) e.preventDefault();

    if (this.getPouchCount() === 0) {
      this.showError(false);
      this.scrollToFlavorSelection();
      return;
    }

    const skioReady = await this.ensureSkioReady();
    if (this.purchaseType === 'subscribe' && this.isSkioEnabled() && !skioReady) {
      this.showError('Could not load subscription box. Please refresh and try again.');
      return;
    }

    const cart = await this.fetchCart();
    const shouldAddFreeGift = this.qualifiesForFreeGift(cart);
    const items = this.buildFlavorCartItems();
    const validationError = this.validateCartItems(items);
    if (validationError) {
      this.showError(validationError);
      return;
    }

    this.showError(false);
    this.addToCartBtn?.classList.add('loading');
    this.addToCartBtn?.setAttribute('aria-disabled', 'true');

    const body = { items };
    const cartComponent = this.getCartComponent();
    if (cartComponent) {
      body.sections = this.getCartSectionIds(cartComponent);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await this.parseCartAddResponse(response);

      if (data?.status) {
        this.showError(data.description || data.message || 'Could not add to cart.');
        return;
      }

      await this.renderCartAfterAdd(data);
      if (shouldAddFreeGift) {
        const cartAfterFlavors = await this.fetchCart();
        if (this.qualifiesForFreeGift(cartAfterFlavors)) {
          await this.addFreeGiftToCart(cartComponent);
        }
      }
      if (cartComponent?.classList.contains('is-empty')) {
        cartComponent.classList.remove('is-empty');
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-june-2026' });
      }
    } catch (err) {
      console.error(err);
      this.showError(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.addToCartBtn?.classList.remove('loading');
      this.updateCheckoutButtons();
    }
  }
}

customElements.define('pdp-june-2026', PdpJune2026);
